import { Designation, RecurringDonationStatus } from "../../types"
import { Schedule } from "source/giving/donate/form/types"
import { ValidationErrors, validate } from "./validations"

export type RecurringDonationState = {
  designations: Designation[]
  selectedPaymentMethodId: string | null
  initialPaymentMethodId: string | null
  schedule: Schedule
  status: RecurringDonationStatus
  initialStatus: RecurringDonationStatus
  releaseHoldAt: string | null
  nextOccurrence: string | null
  validationErrors: ValidationErrors
  liveValidate: boolean
  apiErrorMessage: string | null
}

export type Action =
  | { type: "UPDATE_DESIGNATIONS"; designations: Designation[] }
  | { type: "UPDATE_PAYMENT_METHOD"; paymentMethodId: string | null }
  | { type: "UPDATE_SCHEDULE"; schedule: Schedule }
  | { type: "UPDATE_STATUS"; status: RecurringDonationStatus }
  | { type: "UPDATE_RELEASE_HOLD_AT"; releaseHoldAt: string | null }
  | { type: "UPDATE_NEXT_OCCURRENCE"; nextOccurrence: string | null }
  | { type: "SET_API_ERROR"; errorMessage: string }
  | { type: "CLEAR_ALL_ERRORS" }
  | { type: "START_LIVE_VALIDATION" }
  | { type: "INPUT_BLURRED" }
  | { type: "AMOUNT_CHANGED" }

export function reducer(
  state: RecurringDonationState,
  action: Action,
): RecurringDonationState {
  if (process.env.NODE_ENV === "development") {
    console.debug("[RecurringDonationFormReducer]", { action, state })
  }

  switch (action.type) {
    case "UPDATE_DESIGNATIONS": {
      const newState = {
        ...state,
        designations: action.designations,
      }

      if (state.liveValidate) {
        return {
          ...newState,
          validationErrors: validate({
            designations: action.designations,
            selectedPaymentMethodId: state.selectedPaymentMethodId,
          }),
        }
      } else {
        return newState
      }
    }

    case "UPDATE_PAYMENT_METHOD": {
      const newState = {
        ...state,
        selectedPaymentMethodId: action.paymentMethodId,
      }

      if (state.liveValidate) {
        return {
          ...newState,
          validationErrors: validate({
            designations: state.designations,
            selectedPaymentMethodId: action.paymentMethodId,
          }),
        }
      } else {
        return newState
      }
    }

    case "UPDATE_SCHEDULE": {
      return {
        ...state,
        schedule: action.schedule,
      }
    }

    case "UPDATE_STATUS": {
      const isTransitioningFromHoldToActive =
        state.initialStatus !== "active" && action.status === "active"

      return {
        ...state,
        status: action.status,
        nextOccurrence: isTransitioningFromHoldToActive
          ? null
          : state.nextOccurrence,
      }
    }

    case "UPDATE_RELEASE_HOLD_AT": {
      return {
        ...state,
        releaseHoldAt: action.releaseHoldAt,
      }
    }

    case "UPDATE_NEXT_OCCURRENCE": {
      return {
        ...state,
        nextOccurrence: action.nextOccurrence,
      }
    }

    case "CLEAR_ALL_ERRORS": {
      return {
        ...state,
        validationErrors: {},
        apiErrorMessage: null,
      }
    }

    case "START_LIVE_VALIDATION": {
      return {
        ...state,
        liveValidate: true,
        validationErrors: validate({
          designations: state.designations,
          selectedPaymentMethodId: state.selectedPaymentMethodId,
        }),
      }
    }

    case "INPUT_BLURRED": {
      if (state.liveValidate) {
        return {
          ...state,
          validationErrors: validate({
            designations: state.designations,
            selectedPaymentMethodId: state.selectedPaymentMethodId,
          }),
        }
      } else {
        return state
      }
    }

    case "AMOUNT_CHANGED": {
      if (state.liveValidate) {
        return {
          ...state,
          validationErrors: validate({
            designations: state.designations,
            selectedPaymentMethodId: state.selectedPaymentMethodId,
          }),
        }
      } else {
        return state
      }
    }

    case "SET_API_ERROR": {
      return {
        ...state,
        apiErrorMessage: action.errorMessage,
      }
    }

    default:
      return state
  }
}
